/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import IconArrowRightCopper from "@warthog/XDL/Icons/IconArrowRightCopper";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    isAuthenticated: (state.context.session.isAuthenticated
        || state.context.session.rememberMe)
        && !state.context.session.isGuest,
    signInPage: getPageLinkByPageType(state, "SignInPage")?.url,
    currentLocation: getLocation(state)?.pathname
});

export interface SignUpLinkStyles {
    whiteContainer?: InjectableCss;
    message?: TypographyProps;
    viewLink?: LinkPresentationProps;
}

export const signUpLinkStyles: SignUpLinkStyles = {
    whiteContainer: {
        css: css`
            margin-top: 12px;
            background: white;
            padding: 8px;
            &&:nth-child(2n) {
                margin-top: 20px;
            }
            @media print {
                display: none;
            }
        `,
    },
    message: {
        size: '12px',
        lineHeight: '18px',
        css: css`
            margin-bottom: 12px;
            display: block;
        `,
    },
    viewLink: {
        css: css`
            font-size: 12px;
            line-height: 18px;
            font-weight: bold;
            text-transform: capitalize;

            &:hover, &:focus {
                color: ${getColor('primary')};
            }

            ${IconWrapper} {
                float: right;
                margin-left: 8px;
                position: relative;
            }
        `,
    },
};

const styles = signUpLinkStyles;

const SignUpButton = ({
    isAuthenticated,
    fields: { buttonText },
    signInPage,
    currentLocation,
    history,
    product,
}: Props) => {
    if (isAuthenticated || product.cantBuy) {
        return null;
    }

    const redirectToSignInPage = () => {
        if(signInPage) {
            history.push(`${signInPage}?returnUrl=${currentLocation}`);
        }
    };

    return (
        <StyledWrapper {...styles.whiteContainer}>
            <Typography {...styles.message}>{siteMessage('Custom_UnauthenticatedUserMessage')}</Typography>
            <Clickable onClick={redirectToSignInPage} {...styles.viewLink}>
                {buttonText}
                <Icon src={IconArrowRightCopper} size={9} color="common.black" />
            </Clickable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(withHistory(SignUpButton))),
    definition: {
        displayName: "Sign Up Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Sign Up/Log In",
                fieldType: "General"
            }
        ]
    },
};

export default widgetModule;
